import type { CSSProperties } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { DISCOUNT_BANNER_HEIGHT } from "app/components/discountCampaign/DiscountBanner/StyledDiscountBanner";
import { useAddonsCart } from "app/components/hooks/useAddonsCart";
import { useDiscounts } from "app/components/hooks/useDiscounts";
import { useDunnings } from "app/components/hooks/useDunnings";
import type { PageTopBannerType } from "app/components/PageTopBanner/PageTopBannerType";
import { PageTopBannerDict } from "app/components/PageTopBanner/PageTopBannerType";
import { useTrialBanner } from "app/components/React/TrialBanner/useTrialBanner";
import { FULL_PRIMARY_SIDEBAR_WIDTH } from "app/components/sidebar3/primary-side-bar/styledComponents";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { useSeasonalCampaign } from "app/marketing/hooks/campaing-hooks/seasonal-campaign/useSeasonalCampaign";
import { useProjectsCtaBanner } from "app/pages/activation/components/AddonBanners/ProjectsCtaBanner/useProjectsCtaBanner";
import { NAVIGATION_SIDEBAR_WIDTH } from "app/pages/activation/constants/styles";

const TOP_BANNER_HEIGHTS = {
    [PageTopBannerDict.DISCOUNT_BANNER]: DISCOUNT_BANNER_HEIGHT,
    [PageTopBannerDict.SEASONAL_CAMPAIGN_BANNER]: DISCOUNT_BANNER_HEIGHT,
    [PageTopBannerDict.TRIAL_BANNER]: 54,
    [PageTopBannerDict.DUNNINGS]: 48,
    [PageTopBannerDict.PROJECTS_PAGE_CTA]: 54,
    [PageTopBannerDict.ADDON_CART]: 48,
    [PageTopBannerDict.NONE]: 0,
} as const;

const HOMEPAGES_WITHOUT_SIDEBAR = ["digitalsuite_llm_solutions_homepage", "ranktracker.wizard"];

interface IUsePageTopBannerApi {
    hasTopBanner: boolean;
    pageTopBannerType: PageTopBannerType;
    topBannerHeight: number;
    bannerWrapperStyle: CSSProperties;
}

/**
 * For further information about the banner system please refer to this document:
 * https://similarweb-rnd.atlassian.net/wiki/spaces/PRORD/pages/2525429787/Pro+banner+system
 *
 * Adding a banner:
 * 1. Register its precedence and hook in this module (`pageTopBannerType`).
 * 2. Add its row height to `TOP_BANNER_HEIGHTS` (must match the colored strip’s total height).
 * 3. On the strip’s colored root, spread `{...usePageTopBannerStripReveal()}` (from
 *    `bannerRevealContext.tsx`). `PageTopBanner` then animates the strip’s own height
 *    and fades its content in, with no per-banner animation code.
 */
export const usePageTopBanner = (): IUsePageTopBannerApi => {
    const { state } = useCurrentStateAndParams();
    const hasSecondarySideBar = useMemo(
        () => state.secondaryBarType3 === SecondarySideBarType3.None,
        [state.secondaryBarType3],
    );

    const bannerLeftMargin = useMemo(() => {
        if (HOMEPAGES_WITHOUT_SIDEBAR.includes(state.name ?? "")) {
            return 0;
        }

        if (hasSecondarySideBar) {
            return FULL_PRIMARY_SIDEBAR_WIDTH;
        }

        if (state.isPrimarySidebarAlwaysOpen) {
            return NAVIGATION_SIDEBAR_WIDTH;
        }

        return 0;
    }, [hasSecondarySideBar, state]);

    /** Dunnings */
    const { hasDunningsBanner } = useDunnings();

    /** Discount campaign banner */
    const { hasDiscountCampaign } = useDiscounts();

    /** Trial strip */
    const { hasTrialBanner } = useTrialBanner();

    /** Seasonal campaign banner */
    const { isSeasonalCampaignActive } = useSeasonalCampaign();

    /** Projects Page CTA */
    const { hasProjectsCtaBanner } = useProjectsCtaBanner();

    /** Addons cart */
    const { hasAddonsCartBanner } = useAddonsCart();

    /**
     * This attribute will define which banner should be shown - first matching
     * will be used, so **make sure that you insert your own banners with the
     * correct precedence**. Generally, warning type hooks (such as Dunnings)
     * should be more important than others.
     * Do note that we don't care here about _on which page_ the banner is, that should
     * be already a part of the banner's hook and returned above correctly.
     */
    const pageTopBannerType = useMemo(() => {
        if (hasDunningsBanner) {
            return PageTopBannerDict.DUNNINGS;
        }

        if (isSeasonalCampaignActive) {
            return PageTopBannerDict.SEASONAL_CAMPAIGN_BANNER;
        }

        if (hasDiscountCampaign) {
            return PageTopBannerDict.DISCOUNT_BANNER;
        }

        if (hasTrialBanner) {
            return PageTopBannerDict.TRIAL_BANNER;
        }

        if (hasProjectsCtaBanner) {
            return PageTopBannerDict.PROJECTS_PAGE_CTA;
        }

        if (hasAddonsCartBanner) {
            return PageTopBannerDict.ADDON_CART;
        }

        return PageTopBannerDict.NONE;
    }, [
        hasDunningsBanner,
        hasDiscountCampaign,
        isSeasonalCampaignActive,
        hasTrialBanner,
        hasProjectsCtaBanner,
        hasAddonsCartBanner,
    ]);

    const widthOverride = useMemo(() => {
        if (HOMEPAGES_WITHOUT_SIDEBAR.includes(state.name ?? "")) {
            return "100%";
        }

        return undefined;
    }, [state]);

    return {
        hasTopBanner: pageTopBannerType !== PageTopBannerDict.NONE,
        pageTopBannerType,
        topBannerHeight: TOP_BANNER_HEIGHTS[pageTopBannerType] ?? 0,
        bannerWrapperStyle: { marginLeft: bannerLeftMargin, width: widthOverride },
    };
};
